import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { PlusIcon, PrinterIcon } from "@phosphor-icons/react";
import { TripFormDialog } from "@/components/TripFormDialog";
import { printTripReceipt } from "@/lib/printUtils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const TripsPage = () => {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [trips, setTrips] = useState([]);
  const [cars, setCars] = useState([]);
  const [openForm, setOpenForm] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    const [tr, cs] = await Promise.all([api.get("/trips"), api.get("/cars")]);
    setTrips(tr.data);
    setCars(cs.data);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(
    () => (filter === "all" ? trips : trips.filter((t2) => t2.car_id === filter)),
    [trips, filter]
  );

  const carLabel = (id) => {
    const c = cars.find((x) => x.car_id === id);
    return c ? `${c.plate_number} · ${c.make} ${c.model}` : id;
  };

  const nf = (n) => (n ?? 0).toLocaleString(lang === "ar" ? "ar-EG" : "en-US");

  return (
    <div className="space-y-8" data-testid="trips-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">/log</div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">{t("trips")}</h1>
        </div>
        <div className="flex gap-2 items-center">
          <div className="min-w-[220px]">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger data-testid="trips-filter" className="bg-[#121212] border-white/10 text-sm">
                <SelectValue placeholder={t("filterByCar")} />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border border-white/10 text-white">
                <SelectItem value="all">{t("all")}</SelectItem>
                {cars.map((c) => (
                  <SelectItem key={c.car_id} value={c.car_id}>{c.plate_number} · {c.make} {c.model}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <button
            data-testid="trips-add-btn"
            onClick={() => setOpenForm(true)}
            className="inline-flex items-center gap-2 bg-white text-black px-4 py-3 font-semibold hover:bg-zinc-100 transition-colors"
          >
            <PlusIcon size={16} weight="bold" /> {t("logReturn")}
          </button>
        </div>
      </div>

      <div className="border border-white/10 bg-[#121212] overflow-x-auto">
        <table className="w-full data-table text-sm">
          <thead>
            <tr className="text-zinc-500 text-xs uppercase tracking-[0.2em]">
              <th className="text-start px-4 py-3">{t("date")}</th>
              <th className="text-start px-4 py-3">{t("cars")}</th>
              <th className="text-start px-4 py-3">{t("renterName")}</th>
              <th className="text-start px-4 py-3">{t("returnKm")}</th>
              <th className="text-start px-4 py-3">{t("kmDriven")}</th>
              <th className="text-start px-4 py-3">{t("notes")}</th>
              <th className="text-end px-4 py-3">{t("actions")}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((tr) => (
              <tr key={tr.trip_id} className="hover:bg-[#1C1C1C]" data-testid={`trips-row-${tr.trip_id}`}>
                <td className="px-4 py-3 text-zinc-300">{new Date(tr.return_date).toLocaleString(lang === "ar" ? "ar-EG" : "en-US")}</td>
                <td className="px-4 py-3 font-semibold">{carLabel(tr.car_id)}</td>
                <td className="px-4 py-3 text-zinc-300">{tr.renter_name || "—"}</td>
                <td className="px-4 py-3 font-semibold">{nf(tr.return_km)}</td>
                <td className="px-4 py-3 text-[#39FF14]">+{nf(tr.km_driven)}</td>
                <td className="px-4 py-3 text-zinc-500 max-w-xs truncate">{tr.notes || "—"}</td>
                <td className="px-4 py-3">
                  <div className="flex justify-end">
                    <button
                      onClick={() => {
                        const car = cars.find((c) => c.car_id === tr.car_id);
                        if (car) printTripReceipt({ trip: tr, car, user, lang });
                      }}
                      data-testid={`trips-print-${tr.trip_id}`}
                      title={t("printReceipt")}
                      className="p-2 border border-white/10 hover:bg-[#1C1C1C]"
                    >
                      <PrinterIcon size={14} weight="bold" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-zinc-500">{t("noTrips")}</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <TripFormDialog open={openForm} onOpenChange={setOpenForm} cars={cars} onSaved={() => { setOpenForm(false); load(); }} />
    </div>
  );
};
